import { useMutation } from '@apollo/client'
import ScienceIcon from '@mui/icons-material/Science'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { useCallback, useState } from 'react'
import { ConfirmationDialog } from 'src/components/ConfirmationDialog'
import { graphql } from 'src/gql'
import { AccountStatus, TigersMemberShowQuery } from 'src/gql/graphql'

interface Props {
  member: NonNullable<TigersMemberShowQuery['findMember']>
}

const Mutation = graphql(`
  mutation UpdateIsTestAccountMutation($input: UpdateIsTestAccountInput!) {
    updateIsTestAccount(input: $input) {
      success
      message
      member {
        ... on Member {
          id
          isTestAccount
        }
      }
    }
  }
`)

export const MemberTestAccountButton: React.FC<Props> = ({ member }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const [mutate, { loading }] = useMutation(Mutation, {
    onCompleted: data => {
      if (data.updateIsTestAccount?.success) {
        const action = member.isTestAccount
          ? 'Marked as Test Account'
          : 'Removed Test Account status'
        enqueueSnackbar(`${action} for ${member.name || 'member'}`, {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(
          data.updateIsTestAccount?.message || 'There was an error',
          {
            variant: 'error',
          },
        )
      }
    },
    onError: error => {
      enqueueSnackbar(error.message ?? 'There was an error', {
        variant: 'error',
      })
    },
  })

  const handleClose = () => setDialogOpen(false)
  const handleOpen = () => setDialogOpen(true)

  const confirmOverride = useCallback(
    async (confirmed: boolean) => {
      setConfirmationOpen(false)
      if (!confirmed) return

      await mutate({
        variables: {
          input: {
            memberId: member.id,
            isTestAccount: !member.isTestAccount,
          },
        },
      })

      handleClose()
    },
    [mutate, member.id, member.isTestAccount],
  )

  const isDeactivated = member.accountStatus === AccountStatus.Deactivated
  const willBeTestAccount = !member.isTestAccount

  return (
    <>
      <Button
        startIcon={<ScienceIcon />}
        size="small"
        variant={member.isTestAccount ? 'outlined' : 'contained'}
        color={member.isTestAccount ? 'inherit' : 'secondary'}
        disabled={isDeactivated}
        onClick={handleOpen}>
        {member.isTestAccount ? 'Remove Test Account' : 'Make Test Account'}
      </Button>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {willBeTestAccount ? 'Enable Test Account' : 'Disable Test Account'}
        </DialogTitle>
        <DialogContent>
          {willBeTestAccount ? (
            <Typography variant="body2">
              You are about to mark this account as a test account. This will
              grant the member access to hidden developer features and bypasses in
              the production version of our mobile app.
              <br />
              <br />
              <strong>
                Please confirm that this is NOT a real member before continuing.
              </strong>
            </Typography>
          ) : (
            <Typography variant="body2">
              You are about to remove the test account status from this member.
              They will immediately lose access to all hidden developer features.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={willBeTestAccount ? 'secondary' : 'primary'}
            disabled={loading}
            onClick={() => setConfirmationOpen(true)}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        disabled={loading}
        title="Confirm Status Change"
        message={`Are you sure you want to ${
          willBeTestAccount ? 'enable' : 'disable'
        } test account status for ${member.name}?`}
        open={confirmationOpen}
        onClose={confirmOverride}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </>
  )
}